import {useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {Empty, Flex, Typography} from 'antd'
import InputText from '../../common/InputText'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import useDebouncedValue from '../../../utils/common_function'
import {FOLDER_KEYS, formatFolderLabel} from '../constants'
import BoxTabs from '../BoxTabs'

const { Text } = Typography

const PromotedFilesPreview = ({ record, detail }) => {
  const intl = useIntl()
  const [searchValue, setSearchValue] = useState('')
  const [activeTab, setActiveTab] = useState(null)
  const debouncedSearch = useDebouncedValue(searchValue, 500)

  const sections = useMemo(() => {
    if (!detail) return []
    const { promoted_file_data, folder_data, customer_files } = detail
    const sectionMap = {}

    const customerMakeGroups = [
      customer_files?.length > 0 && {
        label: intl.formatMessage({
          id: 'promoteChanges.customerSetupHeader',
        }),
        files: customer_files.map((fileObj) => fileObj.file_name),
      },
      promoted_file_data?.length > 0 && {
        label: intl.formatMessage({
          id: 'promoteChanges.makeModelFilesHeader',
        }),
        files: promoted_file_data.map((fileObj) => fileObj.file_name),
      },
    ].filter(Boolean)

    sectionMap.customer_make = {
      key: 'customer_make',
      label: intl.formatMessage({
        id: 'promoteChanges.customerSetupAndMakeModel',
      }),
      groups: customerMakeGroups,
    }

    FOLDER_KEYS.forEach((folderKey) => {
      sectionMap[folderKey] = {
        key: folderKey,
        label: formatFolderLabel(folderKey),
        groups: [
          {
            files: (folder_data?.[folderKey] || []).map(
              (fileObj) => fileObj.file_name
            ),
          },
        ],
      }
    })

    return ['customer_make', ...FOLDER_KEYS].map((key) => sectionMap[key])
  }, [detail, intl])

  const effectiveTab =
    activeTab && sections.some((section) => section.key === activeTab)
      ? activeTab
      : sections[0]?.key

  const activeFiles = useMemo(() => {
    const activeSection = sections.find(
      (section) => section.key === effectiveTab
    )
    if (!activeSection) return []
    const files = activeSection.groups.flatMap((group) => group.files)
    if (!debouncedSearch) return [...files].sort()
    return files
      .filter((file_name) =>
        file_name.toLowerCase().includes(debouncedSearch.toLowerCase())
      )
      .sort()
  }, [sections, effectiveTab, debouncedSearch])

  // True when the active section has any files at all, regardless of search.
  const activeSectionHasFiles = useMemo(() => {
    const activeSection = sections.find(
      (section) => section.key === effectiveTab
    )
    return (
      (activeSection?.groups.flatMap((group) => group.files)?.length || 0) > 0
    )
  }, [sections, effectiveTab])

  const includePn = record?.include_pn

  const renderCustomerMakeSection = (section) => (
    <Flex vertical gap={16} className="promote-customer-make-sections">
      {section.groups.length > 0 ? (
        section.groups.map((group, index) => (
          <div
            className={`promote-section-block${
              index === 0 ? ' promote-section-block-customer' : ''
            }`}
            key={group.label}
          >
            <div className="promote-section-header">
              <Text strong>{group.label}</Text>
            </div>
            <div className="promote-section-scroll">
              {group.files.length > 0 ? (
                <Flex wrap="wrap" gap={8} className="promote-files-grid">
                  {[...group.files].sort().map((file_name, index) => (
                    <div
                      key={`${section.key}-${file_name}-${index}`}
                      className="promote-file-check-item"
                    >
                      <EllipsisTooltip className="ellipsis">
                        {file_name}
                      </EllipsisTooltip>
                    </div>
                  ))}
                </Flex>
              ) : (
                <Flex
                  align="center"
                  justify="center"
                  className="promote-empty-state"
                >
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={intl.formatMessage({
                      id: 'promoteChanges.noRecords',
                    })}
                  />
                </Flex>
              )}
            </div>
          </div>
        ))
      ) : (
        <Flex
          align="center"
          justify="center"
          className="promote-empty-state"
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={intl.formatMessage({
              id: 'promoteChanges.noFilesSelected',
            })}
          />
        </Flex>
      )}
    </Flex>
  )

  return (
    <div className="promote-select-right">
      <BoxTabs
        options={sections.map((section) => ({
          label: section.label,
          value: section.key,
        }))}
        value={effectiveTab}
        onChange={(e) => setActiveTab(e.target.value)}
      />
      {sections
        .filter((section) => section.key === effectiveTab)
        .map((section) =>
          section.key === 'customer_make' ? (
            renderCustomerMakeSection(section)
          ) : (
            <Flex vertical gap={8} className="promote-tab-pane">
              <Flex align="center" gap={16} wrap>
                {section.key === 'connector_views' && (
                  <Text type="secondary">
                    {intl.formatMessage(
                      { id: 'promoteChanges.includedPartNumber' },
                      { value: includePn ? 'Yes' : 'No' }
                    )}
                  </Text>
                )}
                <InputText
                  name={`search_${section.key}`}
                  placeholder={intl.formatMessage(
                    { id: 'promoteChanges.searchFiles' },
                    { label: section.label }
                  )}
                  type="search"
                  value={searchValue}
                  isRequired={false}
                  fieldless
                  onChange={(value) => setSearchValue(value)}
                  wrapperClass="promote-section-search"
                />
              </Flex>
              <div className="promote-section-files-list">
                <Flex wrap="wrap" gap={8} className="promote-files-grid">
                  {activeFiles.map((file_name, index) => (
                    <div
                      key={`${section.key}-${file_name}-${index}`}
                      className="promote-file-check-item"
                    >
                      <EllipsisTooltip className="ellipsis">
                        {file_name}
                      </EllipsisTooltip>
                    </div>
                  ))}
                </Flex>
                {activeFiles.length === 0 && (
                  <Flex
                    align="center"
                    justify="center"
                    className="promote-empty-state"
                  >
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={intl.formatMessage({
                        id: activeSectionHasFiles
                          ? 'promoteChanges.noRecords'
                          : 'promoteChanges.noFilesSelected',
                      })}
                    />
                  </Flex>
                )}
              </div>
            </Flex>
          )
        )}
    </div>
  )
}

export default PromotedFilesPreview
